import type { ChatCarryOverOffer, ChatClient, ChatDelivery, ChatMessage, ChatReaction } from '../client';
import {
  createDeliveryStatusElement,
  createMessageElement,
  createStaffJoinedElement,
  renderReactionPills,
  resendFor,
  DELIVERY_STATUS_CLASS,
  MESSAGE_META_CLASS,
  REACTIONS_CLASS,
  type MessageInteractions,
} from './message';
import { spinnerIcon, warningIcon } from './icons';
import { getWidgetLocale, t, tf } from './i18n';

const LIST_CLASS = 'flix-chat-widget__messages';
const TYPING_CLASS = 'flix-chat-widget__typing-indicator';
const LOADING_CLASS = 'flix-chat-widget__messages-loading';
const EMPTY_CLASS = 'flix-chat-widget__messages-empty';
const ERROR_CLASS = 'flix-chat-widget__messages-error';
const ENDED_CLASS = 'flix-chat-widget__messages-ended';
const CARRY_OVER_CLASS = 'flix-chat-widget__carry-over';
const DIVIDER_CLASS = 'flix-chat-widget__date-divider';
const PLACEHOLDER_SELECTOR = `.${LOADING_CLASS}, .${EMPTY_CLASS}, .${ERROR_CLASS}, .${ENDED_CLASS}`;

// How close to the bottom (in pixels) still counts as "at the bottom" —
// scrollHeight/scrollTop math is rarely exact due to subpixel rounding.
const BOTTOM_THRESHOLD_PX = 24;

export interface MessageListRenderer {
  element: HTMLElement;
  showLoading(): void;
  showError(message: string, onRetry: () => void): void;
  showEnded(message: string, onStartNew: () => void): void;
  renderMessages(messages: ChatMessage[]): void;
  appendMessage(message: ChatMessage): void;
  // Messages from the customer's previous chat (whose support channel was
  // deleted) that never reached support, each with one press to send it
  // in this chat; one sent (or gone meanwhile) leaves the list. Kept at the
  // end of the list, apart from the messages themselves.
  showCarryOver(
    offers: ChatCarryOverOffer[],
    send: (offer: ChatCarryOverOffer) => Promise<unknown>,
  ): void;
  // Whether the customer is currently viewing the latest messages — the
  // widget's unread logic treats "at the bottom" as "has seen them".
  isScrolledToBottom(): boolean;
  // A live-only staff-joined notice (see message.ts's createStaffJoinedElement)
  // — never a real ChatMessage, never persisted, so it's a distinct method
  // rather than something appendMessage's ChatMessage-shaped input could
  // carry.
  appendSystemNotice(staffName: string, avatarUrl: string | null): void;
  // Updates a message's reaction pills in place from a live 'message_reaction'
  // event (or the local optimistic echo). No-op for an unknown message id.
  updateReactions(messageId: string, reactions: ChatReaction[]): void;
  // Updates a customer message's delivery mark in place from a live
  // 'message_delivery' event. One that arrives before its message is shown
  // is kept and applied when it is.
  updateDelivery(messageId: string, delivery: ChatDelivery): void;
  // A live "{name} is typing…" row at the end of the list — never a real
  // ChatMessage, same reasoning as appendSystemNotice. widget.ts owns the
  // auto-clear timeout; this is just the render primitive. Automatically
  // cleared by appendMessage (a real message replaces "is typing") and by
  // clear()/showLoading()/etc.
  showTypingIndicator(staffName: string): void;
  hideTypingIndicator(): void;
  scrollToBottom(): void;
}

function dayKey(date: Date): string {
  return date.toDateString();
}

function dayLabel(date: Date): string {
  if (date.toDateString() === new Date().toDateString()) {
    return t('dividerToday');
  }

  return date
    .toLocaleDateString(getWidgetLocale(), { month: 'short', day: 'numeric' })
    .toUpperCase();
}

function createDateDivider(date: Date): HTMLElement {
  const divider = document.createElement('div');
  divider.className = DIVIDER_CLASS;

  const lineBefore = document.createElement('span');
  lineBefore.className = `${DIVIDER_CLASS}-line`;

  const label = document.createElement('span');
  label.className = `${DIVIDER_CLASS}-label`;
  label.textContent = dayLabel(date);

  const lineAfter = document.createElement('span');
  lineAfter.className = `${DIVIDER_CLASS}-line`;

  divider.append(lineBefore, label, lineAfter);
  return divider;
}

/**
 * Owns only the message list container: a loading placeholder, an empty
 * state, an error state, replacing the whole list, and appending one
 * message. It never fetches anything itself — the widget hands it
 * ChatMessage objects. It also owns auto-scroll: history loads and sends
 * always scroll to the bottom, but a live message only scrolls if the
 * reader was already at the bottom, so it never yanks someone away from
 * older messages they're reading. It's also the only place that tracks
 * "same sender/day as the previous message" — message.ts renders exactly
 * one bubble and has no notion of its neighbors.
 */
export function createMessageListRenderer(
  client: ChatClient,
  interactions?: MessageInteractions,
): MessageListRenderer {
  const element = document.createElement('div');
  element.className = LIST_CLASS;
  element.setAttribute('aria-live', 'polite');

  let lastSenderType: string | null = null;
  let lastDayKey: string | null = null;
  // Tracks the live message objects by id so reaction updates can re-render
  // the right pills. Reset with the list.
  const messageIndex = new Map<string, { element: HTMLElement; message: ChatMessage }>();
  // Delivery news for a message not shown yet (it is on its way over the
  // same connection). Kept small: only ever a few at a time.
  const earlyDeliveries = new Map<string, ChatDelivery>();
  let typingElement: HTMLElement | null = null;

  function clear(): void {
    element.replaceChildren();
    lastSenderType = null;
    lastDayKey = null;
    messageIndex.clear();
    typingElement = null;
  }

  function hideTypingIndicator(): void {
    typingElement?.remove();
    typingElement = null;
  }

  function showTypingIndicator(staffName: string): void {
    const wasAtBottom = isScrolledToBottom();

    if (!typingElement) {
      typingElement = document.createElement('div');
      typingElement.className = TYPING_CLASS;
      typingElement.setAttribute('aria-live', 'polite');

      const label = document.createElement('span');
      label.className = `${TYPING_CLASS}-label`;

      const dots = document.createElement('span');
      dots.className = `${TYPING_CLASS}-dots`;
      dots.append(
        document.createElement('span'),
        document.createElement('span'),
        document.createElement('span'),
      );

      typingElement.append(label, dots);
    }

    const label = typingElement.querySelector<HTMLElement>(`.${TYPING_CLASS}-label`);
    if (label) {
      label.textContent = tf('staffTyping', { name: staffName });
    }

    // Always the trailing element — re-appending an already-attached node
    // just moves it, so this keeps it last even if a message arrived and
    // got appended after this indicator was first created.
    element.append(typingElement);

    if (wasAtBottom) {
      scrollToBottom();
    }
  }

  function isScrolledToBottom(): boolean {
    return element.scrollHeight - element.scrollTop - element.clientHeight <= BOTTOM_THRESHOLD_PX;
  }

  function scrollToBottom(): void {
    element.scrollTop = element.scrollHeight;
  }

  function showLoading(): void {
    clear();

    const loading = document.createElement('div');
    loading.className = LOADING_CLASS;

    const widths: Array<{ side: 'staff' | 'customer'; width: string }> = [
      { side: 'staff', width: '62%' },
      { side: 'customer', width: '70%' },
      { side: 'staff', width: '48%' },
      { side: 'customer', width: '55%' },
    ];

    for (const { side, width } of widths) {
      const row = document.createElement('div');
      row.className = `${LOADING_CLASS}-row${side === 'customer' ? ` ${LOADING_CLASS}-row--customer` : ''}`;

      if (side === 'staff') {
        const avatar = document.createElement('span');
        avatar.className = `${LOADING_CLASS}-avatar`;
        row.append(avatar);
      }

      const bubble = document.createElement('span');
      bubble.className = `${LOADING_CLASS}-bubble`;
      bubble.style.width = width;
      row.append(bubble);

      loading.append(row);
    }

    element.append(loading);
  }

  function showEmptyState(): void {
    clear();

    const empty = document.createElement('div');
    empty.className = EMPTY_CLASS;

    const icon = document.createElement('div');
    icon.className = `${EMPTY_CLASS}-icon`;
    icon.textContent = '👋';

    const title = document.createElement('div');
    title.className = `${EMPTY_CLASS}-title`;
    title.textContent = t('emptyTitle');

    const description = document.createElement('p');
    description.style.margin = '0';
    description.textContent = t('emptyDescription');

    empty.append(icon, title, description);
    element.append(empty);
  }

  function showError(message: string, onRetry: () => void): void {
    clear();

    const error = document.createElement('div');
    error.className = ERROR_CLASS;

    const icon = document.createElement('div');
    icon.className = `${ERROR_CLASS}-icon`;
    icon.append(warningIcon());

    const title = document.createElement('div');
    title.className = `${ERROR_CLASS}-title`;
    title.textContent = message;

    const retryButton = document.createElement('button');
    retryButton.type = 'button';
    retryButton.textContent = t('tryAgain');
    retryButton.addEventListener('click', () => onRetry());

    error.append(icon, title, retryButton);
    element.append(error);
  }

  function showEnded(message: string, onStartNew: () => void): void {
    clear();

    const ended = document.createElement('div');
    ended.className = ENDED_CLASS;

    const title = document.createElement('div');
    title.className = `${ENDED_CLASS}-title`;
    title.textContent = message;

    const startNewButton = document.createElement('button');
    startNewButton.type = 'button';
    startNewButton.textContent = t('startNewChat');
    // This element is always discarded, not re-enabled: onStartNew's caller
    // (connectToConversation) either succeeds — renderMessages replaces
    // this whole subtree — or fails — showError replaces it instead. So a
    // one-way disable-and-spin here is sufficient; there's no path back to
    // an enabled version of this exact button.
    startNewButton.addEventListener(
      'click',
      () => {
        startNewButton.disabled = true;
        startNewButton.setAttribute('aria-busy', 'true');
        startNewButton.prepend(spinnerIcon());
        onStartNew();
      },
      { once: true },
    );

    ended.append(title, startNewButton);
    element.append(ended);
  }

  // Shared by renderMessages/appendMessage: decides whether this message
  // needs a fresh date divider and/or a visible avatar, based purely on
  // what immediately preceded it, then updates that tracking state.
  function appendOne(message: ChatMessage): void {
    const createdAt = new Date(message.createdAt);
    const key = Number.isNaN(createdAt.getTime()) ? null : dayKey(createdAt);

    if (key !== null && key !== lastDayKey) {
      element.append(createDateDivider(createdAt));
      lastDayKey = key;
      lastSenderType = null;
    }

    const showAvatar = message.senderType !== lastSenderType;
    const early = earlyDeliveries.get(message.id);
    if (early) {
      earlyDeliveries.delete(message.id);
      message = { ...message, delivery: early };
    }
    const messageElement = createMessageElement(message, client, showAvatar, interactions);
    element.append(messageElement);
    messageIndex.set(message.id, { element: messageElement, message });
    lastSenderType = message.senderType;
  }

  function updateReactions(messageId: string, reactions: ChatReaction[]): void {
    const tracked = messageIndex.get(messageId);
    if (!tracked || !interactions) {
      return;
    }

    tracked.message = { ...tracked.message, reactions };
    const container = tracked.element.querySelector<HTMLElement>(`.${REACTIONS_CLASS}`);
    if (container) {
      renderReactionPills(container, tracked.message, interactions);
    }
  }

  function updateDelivery(messageId: string, delivery: ChatDelivery): void {
    const tracked = messageIndex.get(messageId);
    if (!tracked) {
      if (earlyDeliveries.size < 50) earlyDeliveries.set(messageId, delivery);
      return;
    }

    tracked.message = { ...tracked.message, delivery };
    const mark = createDeliveryStatusElement(delivery, resendFor(tracked.message, interactions));
    const current = tracked.element.querySelector<HTMLElement>(`.${DELIVERY_STATUS_CLASS}`);
    if (current) {
      current.replaceWith(mark);
    } else {
      tracked.element.querySelector<HTMLElement>(`.${MESSAGE_META_CLASS}`)?.append(mark);
    }
  }

  function renderMessages(messages: ChatMessage[]): void {
    if (messages.length === 0) {
      showEmptyState();
      return;
    }

    clear();

    for (const message of messages) {
      appendOne(message);
    }

    scrollToBottom();
  }

  function appendMessage(message: ChatMessage): void {
    const wasAtBottom = isScrolledToBottom();
    hideTypingIndicator();

    const carryOver = element.querySelector(`.${CARRY_OVER_CLASS}`);

    if (element.querySelector(PLACEHOLDER_SELECTOR)) {
      clear();
    }

    appendOne(message);
    if (carryOver) element.append(carryOver);

    if (wasAtBottom) {
      scrollToBottom();
    }
  }

  function showCarryOver(
    offers: ChatCarryOverOffer[],
    send: (offer: ChatCarryOverOffer) => Promise<unknown>,
  ): void {
    element.querySelector(`.${CARRY_OVER_CLASS}`)?.remove();
    if (offers.length === 0) {
      return;
    }

    const panel = document.createElement('section');
    panel.className = CARRY_OVER_CLASS;
    const title = document.createElement('div');
    title.className = `${CARRY_OVER_CLASS}-title`;
    title.textContent = t('carryOverTitle');
    panel.append(title);

    for (const offer of offers) {
      const item = document.createElement('div');
      item.className = `${CARRY_OVER_CLASS}-item`;
      const text = document.createElement('div');
      text.className = `${CARRY_OVER_CLASS}-text`;
      // As the customer wrote it, whatever its language.
      text.dir = 'auto';
      text.textContent = offer.body.trim();
      if (offer.attachmentCount > 0) {
        const files = document.createElement('span');
        files.className = `${CARRY_OVER_CLASS}-files`;
        files.textContent = tf('carryOverFiles', { count: offer.attachmentCount });
        text.append(files);
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = `${CARRY_OVER_CLASS}-send`;
      button.textContent = t('resendMessage');
      button.addEventListener('click', () => {
        // One press, one request: the server makes the new message once
        // only, whatever another tab does at the same moment.
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        send(offer).then(
          () => {
            item.remove();
            if (!panel.querySelector(`.${CARRY_OVER_CLASS}-item`)) panel.remove();
          },
          () => {
            button.disabled = false;
            button.removeAttribute('aria-busy');
          },
        );
      });

      item.append(text, button);
      panel.append(item);
    }

    element.append(panel);
  }

  function appendSystemNotice(staffName: string, avatarUrl: string | null): void {
    const wasAtBottom = isScrolledToBottom();

    if (element.querySelector(PLACEHOLDER_SELECTOR)) {
      clear();
    }

    element.append(createStaffJoinedElement(staffName, avatarUrl));
    // Same reasoning as a date divider: a visually distinct break in the
    // stream, so the next real message re-shows its avatar even if it's
    // from the same sender as whatever came right before this notice.
    lastSenderType = null;

    if (wasAtBottom) {
      scrollToBottom();
    }
  }

  return {
    element,
    showLoading,
    showError,
    showEnded,
    renderMessages,
    appendMessage,
    showCarryOver,
    appendSystemNotice,
    updateReactions,
    updateDelivery,
    showTypingIndicator,
    hideTypingIndicator,
    isScrolledToBottom,
    scrollToBottom,
  };
}
